import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  alpha,
  useTheme,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  MonetizationOn as MonetizationOnIcon,
  BookmarkBorder as BookmarkBorderIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import apiService from '../../../services/apiService';
import { getVoucherTemplates, VoucherTemplate } from '../../../services/financeService';

// واجهة الحساب للقائمة المنسدلة
interface AccountOption {
  id: string;
  accountNumber: string;
  name: string;
  label: string;
  type?: string;
  isActive?: boolean;
}

// واجهة بيانات سند الصرف للنموذج
interface PaymentData {
  id?: string;
  paymentNumber?: string;
  date: string;
  status: 'مؤكد' | 'تحت الموافقة' | 'مرفوض';
  debitAccountId: string;
  creditAccountId: string;
  amount: number;
  description: string;
}

// واجهة بيانات سند الصرف من واجهة برمجة التطبيقات
interface PaymentApiResponse {
  _id: string;
  paymentNumber: string;
  date: string;
  status: 'مؤكد' | 'تحت الموافقة' | 'مرفوض';
  debitAccount: { _id: string; name: string; accountNumber?: string } | string;
  creditAccount: { _id: string; name: string; accountNumber?: string } | string;
  amount: number;
  description: string;
  createdBy?: { name: string; _id: string };
  createdAt?: string;
  approvedBy?: { name: string; _id: string };
  approvedAt?: string;
}

// نموذج سند صرف فارغ للإنشاء
const emptyPayment: PaymentData = {
  date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
  status: 'تحت الموافقة',
  debitAccountId: '',
  creditAccountId: '',
  amount: 0,
  description: ''
};

// تحويل حسابات API إلى تنسيق القائمة المنسدلة
const mapApiAccountsToOptions = (accounts: any[]): AccountOption[] => {
  if (!Array.isArray(accounts)) {
    console.error('حسابات غير صالحة:', accounts);
    return [];
  }
  
  return accounts.map(account => {
    if (!account) return null;
    
    const id = account.id || account._id;
    const accountNumber = account.accountNumber || '0000';
    const name = account.name || 'حساب غير معروف';
    
    if (!id) {
      console.error('حساب بدون معرف:', account);
      return null;
    }
    
    return {
      id,
      accountNumber,
      name,
      label: `${accountNumber} - ${name}`,
      type: account.type,
      isActive: account.isActive !== undefined ? account.isActive : true
    };
  }).filter(account => account !== null) as AccountOption[];
};

const PaymentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { activeBranch } = useAuth();
  const isEditMode = Boolean(id);
  
  // حالة النموذج
  const [formData, setFormData] = useState<PaymentData>(emptyPayment);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [templates, setTemplates] = useState<VoucherTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<VoucherTemplate | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  
  // جلب الحسابات من API
  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        // استدعاء API لجلب جميع الحسابات النشطة مع السياق المناسب
        const { apiWithContext } = await import('../../../services/api');
        const response = await apiWithContext.get('/finance/accounts/selection/active', 'payment-voucher').then(res => res.data);
        console.log('API response for accounts:', response);
        
        // التحقق من صحة البيانات المستلمة
        if (Array.isArray(response) && response.length > 0) {
          setAccounts(mapApiAccountsToOptions(response));
        } else {
          // البديل: استخدام API مختلف لجلب الحسابات
          const allAccounts = await apiService.get<any[]>('/finance/accounts?isActive=true');
          console.log('Fallback API response for accounts:', allAccounts);
          
          if (Array.isArray(allAccounts) && allAccounts.length > 0) {
            setAccounts(mapApiAccountsToOptions(allAccounts));
          } else {
            console.error('لا توجد حسابات متوفرة');
          }
        }
      } catch (error) {
        console.error('خطأ في جلب الحسابات:', error);
        // محاولة استخدام API بديل
        try {
          const allAccounts = await apiService.get<any[]>('/finance/accounts?isActive=true');
          if (Array.isArray(allAccounts) && allAccounts.length > 0) {
            setAccounts(mapApiAccountsToOptions(allAccounts));
          }
        } catch (fallbackError) {
          console.error('فشلت محاولة جلب الحسابات البديلة:', fallbackError);
        }
      } finally {
        setAccountsLoading(false);
      }
    };
    
    fetchAccounts();
  }, []);
  
  // جلب القوالب المحفوظة
  useEffect(() => {
    const fetchTemplates = async () => {
      if (isEditMode) return; // لا نحتاج القوالب في وضع التعديل
      
      setTemplatesLoading(true);
      try {
        const templatesData = await getVoucherTemplates({ templateType: 'payment', isActive: true });
        setTemplates(templatesData);
      } catch (error) {
        console.error('خطأ في جلب القوالب:', error);
      } finally {
        setTemplatesLoading(false);
      }
    };
    
    fetchTemplates();
  }, [isEditMode]);
  
  // تطبيق القالب المختار
  const handleTemplateSelect = (template: VoucherTemplate | null) => {
    setSelectedTemplate(template);
    
    if (template) {
      // ملء الحسابات من القالب
      if (template.debitAccount) {
        setFormData(prev => ({
          ...prev,
          debitAccountId: template.debitAccount!.id
        }));
      }
      
      if (template.creditAccount) {
        setFormData(prev => ({
          ...prev,
          creditAccountId: template.creditAccount!.id
        }));
      }
      
      // مسح أي أخطاء متعلقة بالحسابات
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.debitAccountId;
        delete newErrors.creditAccountId;
        return newErrors;
      });
    }
  };
  
  // جلب بيانات سند الصرف عند التعديل
  useEffect(() => {
    if (isEditMode && id) {
      setLoading(true);
      
      const fetchPayment = async () => {
        try {
          const payment = await apiService.get<PaymentApiResponse>(`/finance/payments/${id}`);
          
          setFormData({
            id: payment._id,
            paymentNumber: payment.paymentNumber || '',
            date: payment.date.split('T')[0], // تحويل التاريخ إلى التنسيق المناسب
            status: payment.status,
            debitAccountId: typeof payment.debitAccount === 'string'
              ? payment.debitAccount
              : payment.debitAccount._id,
            creditAccountId: typeof payment.creditAccount === 'string'
              ? payment.creditAccount
              : payment.creditAccount._id,
            amount: payment.amount,
            description: payment.description
          });
        } catch (error) {
          console.error('خطأ في جلب بيانات سند الصرف:', error);
          setErrors(prev => ({
            ...prev,
            submit: 'تعذر جلب بيانات سند الصرف'
          }));
        } finally {
          setLoading(false);
        }
      };
      
      fetchPayment();
    }
  }, [id, isEditMode]);
  
  // معالجة تغيير حقول النص والأرقام
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // مسح رسالة الخطأ عند التغيير
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // معالجة تغيير التاريخ
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    
    setFormData(prev => ({ ...prev, date: value }));
    
    // مسح رسالة الخطأ عند التغيير
    if (errors.date) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.date;
        return newErrors;
      });
    }
  };
  
  // معالجة تغيير الحساب المدين
  const handleDebitAccountChange = (event: React.SyntheticEvent, value: AccountOption | null) => {
    if (value) {
      setFormData(prev => ({ ...prev, debitAccountId: value.id }));
      
      // مسح رسالة الخطأ عند التغيير
      if (errors.debitAccountId) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.debitAccountId;
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, debitAccountId: '' }));
    }
  };
  
  // معالجة تغيير الحساب الدائن
  const handleCreditAccountChange = (event: React.SyntheticEvent, value: AccountOption | null) => {
    if (value) {
      setFormData(prev => ({ ...prev, creditAccountId: value.id }));
      
      // مسح رسالة الخطأ عند التغيير
      if (errors.creditAccountId) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.creditAccountId;
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, creditAccountId: '' }));
    }
  };
  
  // التحقق من صحة البيانات
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.date) {
      newErrors.date = 'تاريخ السند مطلوب';
    }
    
    if (!formData.debitAccountId) {
      newErrors.debitAccountId = 'الحساب المدين مطلوب';
    }
    
    if (!formData.creditAccountId) {
      newErrors.creditAccountId = 'الحساب الدائن مطلوب';
    }
    
    if (formData.debitAccountId === formData.creditAccountId) {
      newErrors.creditAccountId = 'الحساب الدائن يجب أن يكون مختلفًا عن الحساب المدين';
    }
    
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'المبلغ يجب أن يكون أكبر من صفر';
    }
    
    // البيان أصبح اختيارياً - تم إزالة التحقق من الإلزامية
    
    // مسح رسالة خطأ التقديم السابقة إن وجدت
    if (errors.submit) {
      delete newErrors.submit;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // معالجة حفظ النموذج
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaveLoading(true);
    
    try {
      // التحقق من وجود الفرع النشط
      if (!activeBranch) {
        setErrors({ general: 'يجب تحديد فرع نشط قبل حفظ سند الصرف' });
        setSaveLoading(false);
        return;
      }

      // Get branch timezone
      const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';

      // Convert date to branch timezone with current time
      let dateToSend: string;
      if (formData.date) {
        const [year, month, day] = formData.date.split('-').map(Number);
        const now = moment.tz(branchTimezone);
        const dateWithTime = moment.tz({
          year,
          month: month - 1,
          day,
          hour: now.hour(),
          minute: now.minute(),
          second: now.second()
        }, branchTimezone);
        dateToSend = dateWithTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      } else {
        dateToSend = moment.tz(branchTimezone).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      }

      // إعداد البيانات للإرسال للواجهة الخلفية - تصحيح أسماء الحقول
      const paymentData = {
        date: dateToSend,
        debitAccount: formData.debitAccountId,  // اسم الحقل الصحيح متوافق مع الخادم
        creditAccount: formData.creditAccountId, // اسم الحقل الصحيح متوافق مع الخادم
        amount: Number(formData.amount), // تحويل المبلغ إلى رقم
        description: formData.description,
        status: 'تحت الموافقة',
        branchId: activeBranch._id,
        branchName: activeBranch.name
      };

      console.log('بيانات السند المرسلة:', paymentData); // إضافة سجل لتتبع البيانات المرسلة

      // التحقق من الحسابات قبل الإرسال للمساعدة في تشخيص الأخطاء
      if (!formData.debitAccountId || !formData.creditAccountId) {
        throw new Error('يجب تحديد الحسابات المدينة والدائنة بشكل صحيح');
      }

      // وضع الإجراء في كتلة try/catch منفصلة للحصول على تفاصيل أفضل للخطأ
      try {
        if (isEditMode && id) {
          // تحديث سند صرف موجود
          await apiService.put(`/finance/payments/${id}`, paymentData);
        } else {
          // إنشاء سند صرف جديد
          console.log('بدء إرسال طلب إنشاء سند صرف جديد...');
          try {
            const response = await apiService.post('/finance/payments', paymentData);
            console.log('تم إنشاء سند الصرف بنجاح:', response);
          } catch (postError: any) {
            console.error('تفاصيل خطأ طلب POST:', {
              status: postError.response?.status,
              data: postError.response?.data,
              message: postError.message
            });
            throw postError;
          }
        }
      } catch (apiError: any) {
        console.error('تفاصيل خطأ API:', {
          status: apiError.response?.status,
          data: apiError.response?.data,
          message: apiError.message
        });
        throw apiError;
      }
      
      setSaveLoading(false);
      setSuccess(true);
      
      // العودة إلى صفحة القائمة بعد النجاح
      setTimeout(() => {
        navigate('/finance/payments');
      }, 1500);
    } catch (error: any) {
      console.error('خطأ في حفظ سند الصرف:', error);
      setSaveLoading(false);
      
      // تحليل رسالة الخطأ بشكل أفضل
      let errorMessage = 'حدث خطأ أثناء حفظ سند الصرف';
      let errorDetails = '';
      
      if (error.response) {
        // خطأ من الخادم
        const statusCode = error.response.status;
        
        if (statusCode === 401) {
          errorMessage = 'تم انتهاء جلسة عملك أو لم يتم تسجيل الدخول. يرجى تسجيل الدخول مرة أخرى.';
        } else if (statusCode === 400) {
          // أخطاء التحقق من صحة البيانات
          errorMessage = error.response.data.message || 'البيانات المدخلة غير صالحة';
          if (error.response.data.details) {
            errorDetails = error.response.data.details;
          }
        } else if (statusCode === 404) {
          errorMessage = 'لم يتم العثور على المورد المطلوب';
        } else if (statusCode === 500) {
          errorMessage = 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقًا.';
          if (error.response.data.error) {
            errorDetails = error.response.data.error;
          }
        } else {
          errorMessage = error.response.data.message || 'حدث خطأ أثناء حفظ سند الصرف';
        }
      } else if (error.request) {
        // لا توجد استجابة من الخادم
        errorMessage = 'تعذر الاتصال بالخادم. تحقق من اتصالك بالإنترنت وحاول مرة أخرى.';
      } else if (error.message) {
        // أخطاء أخرى
        errorMessage = error.message;
      }
      
      // عرض رسالة خطأ مفصلة للمستخدم
      setErrors(prev => ({
        ...prev,
        submit: errorDetails ? `${errorMessage} - ${errorDetails}` : errorMessage
      }));
      
      // عرض رسالة في وحدة التحكم للمطورين للمساعدة في التشخيص
      console.log('معلومات تشخيصية:', {
        رمز_الاستجابة: error.response?.status,
        بيانات_الخطأ: error.response?.data,
        رسالة: error.message,
        بيانات_النموذج: formData
      });
    }
  };
  
  // العنوان بناءً على وضع النموذج
  const title = isEditMode ? 'تعديل سند صرف' : 'إضافة سند صرف جديد';

  // الحصول على الحساب بواسطة المعرّف
  const getAccountById = (id: string) => {
    return accounts.find(account => account.id === id);
  };
  
  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        {/* العنوان ومسار التنقل */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 1 }}>
              <PaymentIcon sx={{ mr: 1, color: '#30c3b0' }} />
              {title}
            </Typography>
            
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link to="/finance/payments" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                سندات الصرف
              </Link>
              <Typography color="text.primary">{title}</Typography>
            </Breadcrumbs>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/finance/payments"
            sx={{
              borderColor: '#30c3b0',
              color: '#30c3b0',
              '&:hover': { borderColor: '#25a899', backgroundColor: alpha('#30c3b0', 0.04) },
            }}
          >
            العودة للقائمة
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* عرض التحميل أثناء جلب البيانات */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* قسم اختيار القالب - يظهر فقط في وضع الإنشاء */}
            {!isEditMode && (
              <Box sx={{ mb: 3, p: 2, bgcolor: alpha('#30c3b0', 0.05), borderRadius: 2, border: '1px dashed', borderColor: '#30c3b0' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', color: '#30c3b0' }}>
                  <BookmarkBorderIcon sx={{ mr: 1 }} />
                  استخدام قالب محفوظ (اختياري)
                </Typography>
                
                <Autocomplete
                  options={templates}
                  getOptionLabel={(option) => option.name}
                  value={selectedTemplate}
                  onChange={(_, value) => handleTemplateSelect(value)}
                  loading={templatesLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="اختر قالب محفوظ لملء الحسابات تلقائياً"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {templatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {option.name}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block" color="text.secondary">
                          المدين: {option.debitAccount?.name} | الدائن: {option.creditAccount?.name}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                
                {selectedTemplate && (
                  <Alert severity="info" sx={{ mt: 2 }} icon={<BookmarkBorderIcon />}>
                    تم تطبيق القالب "{selectedTemplate.name}" - الحسابات المدينة والدائنة تم ملؤها تلقائياً
                  </Alert>
                )}
              </Box>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    معلومات السند الأساسية
                  </Typography>
                  
                  {isEditMode && formData.paymentNumber && (
                    <TextField
                      name="paymentNumber"
                      label="رقم السند"
                      value={formData.paymentNumber}
                      fullWidth
                      margin="normal"
                      disabled
                      InputProps={{
                        startAdornment: (
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            #
                          </Typography>
                        ),
                      }}
                    />
                  )}
                  
                  <TextField
                    name="date"
                    label="تاريخ السند"
                    type="date"
                    value={formData.date}
                    onChange={handleDateChange}
                    error={!!errors.date}
                    helperText={errors.date}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  
                  {isEditMode && (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>حالة السند</InputLabel>
                      <Select
                        value={formData.status}
                        label="حالة السند"
                        disabled
                      >
                        <MenuItem value="مؤكد">مؤكد</MenuItem>
                        <MenuItem value="تحت الموافقة">تحت الموافقة</MenuItem>
                        <MenuItem value="مرفوض">مرفوض</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    التفاصيل المالية
                  </Typography>
                  
                  {accounts.length === 0 && !accountsLoading && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      لم يتم العثور على حسابات. تأكد من وجود حسابات مفعّلة في النظام.
                    </Alert>
                  )}
                  
                  <Autocomplete
                    options={accounts}
                    getOptionDisabled={(option) => option.type === 'رئيسي'}
                    getOptionLabel={(option) => option.label}
                    value={formData.debitAccountId ? getAccountById(formData.debitAccountId) : null}
                    onChange={handleDebitAccountChange}
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="الحساب المدين (المصروف أو وجهة الصرف)"
                        margin="normal"
                        error={!!errors.debitAccountId}
                        helperText={errors.debitAccountId || "اختر الحساب المدين (يظهر الحساب الرئيسي ولكن لا يمكن اختياره)"}
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {accountsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  
                  <Autocomplete
                    options={accounts}
                    getOptionDisabled={(option) => option.type === 'رئيسي'}
                    getOptionLabel={(option) => option.label}
                    value={formData.creditAccountId ? getAccountById(formData.creditAccountId) : null}
                    onChange={handleCreditAccountChange}
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="الحساب الدائن (مصدر الصرف)"
                        margin="normal"
                        error={!!errors.creditAccountId}
                        helperText={errors.creditAccountId || "اختر الحساب الدائن (يظهر الحساب الرئيسي ولكن لا يمكن اختياره)"}
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {accountsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  
                  <TextField
                    name="amount"
                    label="المبلغ"
                    value={formData.amount}
                    onChange={handleInputChange}
                    type="number"
                    InputProps={{
                      endAdornment: <Typography sx={{ ml: 1 }}>ريال</Typography>,
                    }}
                    error={!!errors.amount}
                    helperText={errors.amount}
                    fullWidth
                    margin="normal"
                    required
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="البيان (اختياري)"
                  value={formData.description}
                  onChange={handleInputChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            {/* رسالة النجاح */}
            {success && (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                sx={{ mb: 3 }}
              >
                تم {isEditMode ? 'تعديل' : 'إضافة'} سند الصرف بنجاح
              </Alert>
            )}
            
            {/* رسالة الخطأ */}
            {errors.submit && (
              <Alert
                severity="error"
                sx={{ mb: 3 }}
              >
                {errors.submit}
              </Alert>
            )}
            
            {/* أزرار الإجراءات */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/finance/payments')}
                disabled={saveLoading}
              >
                إلغاء
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
                disabled={saveLoading || success}
                sx={{
                  bgcolor: '#30c3b0',
                  '&:hover': { bgcolor: '#25a899' },
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
                }}
              >
                {saveLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  isEditMode ? 'حفظ التغييرات' : 'إضافة السند'
                )}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentForm;